import { create } from 'zustand'
import type { NonEmptyArray } from 'effect/Array'
import { Array as A, Option as O, pipe } from 'effect'
import { original, setAutoFreeze } from 'immer'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { createSelector } from 'reselect'
import type { Game, MarbleGame } from 'src/types/domain/Game'
import { isMarbleGame, isUnknownGame, unknownGame } from 'src/types/domain/Game'
import type { Race } from 'src/types/domain/Race'
import type { RealtimeTrackDTO, RealtimeTrackModeDTO } from 'src/services/supabase/queries'
import { TrackMap } from 'src/types/mappers/TrackMap'
import { TrackModeMap } from 'src/types/mappers/TrackModeMap'
import { GameMap } from 'src/types/mappers/GameMap'
import { MarbleMap } from 'src/types/mappers/MarbleMap'
import { RaceEventMap } from 'src/types/mappers/RaceEventMap'
import { RaceMap } from 'src/types/mappers/RaceMap'
import type { RaceEvent } from 'src/types/domain/RaceEvent'
import type { GameConfigDTO, TokenGameConfigDTO } from 'src/actions/types'
import { getQueryParam } from 'src/utils/qetQueryParam'
import { getI18nSettings } from 'src/services/i18n'
import { isKnownGameConfig, type GameConfig } from 'src/types/domain/GameConfig'
import type { Player } from 'src/types/domain/Player'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import { getLatestEvent } from 'src/utils/raceUtils'
import { normalizeNullableVideo } from 'src/types/mappers/TrackMap/TrackMap'
import { movesPointerForward } from './utils/movesPointerForward'
import { mergeRaceIdsByRecency, unionFetchedIds } from './utils/mergeFetchedIds'
import {
  type NormalizedMarbleGame,
  type NormalizedMarble,
  type NormalizedRace,
  type NormalizedRaceEvent,
  type NormalizedTrack,
  type NormalizedTrackMode,
  orderRacesByCreatedAt,
  orderRaceEventsByTimestamp
} from './types'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'
import { useSocialStore } from './social'
import { isDefined } from './utils/isDefined'
import { useSocialStreamStore } from './socialStream'

setAutoFreeze(false)

export interface GameState {
  currentGameId: string | undefined
  currentLanguage: string
  config: GameConfig
  games: {
    isFetched: boolean
    byId: Record<string, NormalizedMarbleGame>
    allIds: string[]
    playableIds: string[]
    allowedGameIds: string[] | undefined
  }
  marbles: {
    byId: Record<string, NormalizedMarble>
    allIds: string[]
  }
  races: {
    byId: Record<string, NormalizedRace>
    allIds: string[]
  }
  raceEvents: {
    byId: Record<string, NormalizedRaceEvent>
    allIds: string[]
  }
  tracks: {
    byId: Record<string, NormalizedTrack>
    allIds: string[]
  }
  trackModes: {
    byId: Record<string, NormalizedTrackMode>
    allIds: string[]
  }
}

export interface GameActions {
  gameUpdated: (game: Partial<NormalizedMarbleGame> & { id: string; type: string }) => void
  raceAdded: (race: NormalizedRace) => void
  raceUpdated: (race: Partial<NormalizedRace> & { id: string }) => void
  raceEventAdded: (raceEvent: RaceEvent) => void
  raceEventUpdated: (raceEvent: RaceEvent) => void
  tokenGameConfigRetrieved: (tokenGameConfig: TokenGameConfigDTO) => void
  updateGameById: (game: Partial<MarbleGame> & { id: string }) => void
  updateTrack: (payload: RealtimeTrackDTO) => void
  updateTrackMode: (payload: RealtimeTrackModeDTO) => void
  setRaces: (gameId: string, races: NonEmptyArray<Race>) => void
  setCurrentGame: (game: Game) => void
  setGames: (games: Record<string, MarbleGame>) => void
  setPlayableGames: (games: Record<string, MarbleGame>, currentGameId?: string) => void
  setAllowedGameIds: (ids: string[]) => void
  setCurrentLanguage: (language: string) => void
  setGameConfig: (gameConfig: GameConfigDTO) => void
}

type GameStore = GameState & { actions: GameActions }

const { fallbackLng } = getI18nSettings()
const addRaceEventToExistingRace = (state: GameState, raceEvent: NormalizedRaceEvent) => {
  const race = state.races.byId[raceEvent.raceId]

  if (!race) {
    return
  }

  const sortedRaceEventIds = pipe(
    race.raceEventIds,
    A.map((id) => state.raceEvents.byId[id]),
    A.map(O.fromNullable),
    A.getSomes,
    A.append(raceEvent),
    A.sortBy(orderRaceEventsByTimestamp),
    A.map((re) => re.uuid)
  )

  race.raceEventIds = sortedRaceEventIds
}

const addRaceToExistingGame = (state: GameState, race: NormalizedRace) => {
  const game = state.games.byId[race.raceConfigurationId]

  if (!game) {
    return
  }

  const sortedRaceIds = pipe(
    game.raceIds,
    A.map((id) => state.races.byId[id]),
    A.map(O.fromNullable),
    A.getSomes,
    A.append(race),
    A.sortBy(orderRacesByCreatedAt),
    A.map((r) => r.id)
  )

  game.raceIds = sortedRaceIds
}

const removeRaceFromGame = ({
  state,
  previousRace,
  nextRace
}: {
  state: GameState
  previousRace?: NormalizedRace
  nextRace: NormalizedRace
}): void => {
  if (!previousRace) {
    return
  }

  if (previousRace.id !== nextRace.id) {
    console.error('Should not happen, prev and next race are different:', {
      previousRace,
      nextRace
    })
    return
  }

  const game = state.games.byId[previousRace.raceConfigurationId]

  if (!game) {
    return
  }

  if (previousRace.raceConfigurationId !== nextRace.raceConfigurationId) {
    // remove race result from old race
    // ? How to fix this ?
    game.raceIds = game.raceIds.filter((id) => id !== nextRace.id) as NonEmptyArray<string>
  }
}

const removeRaceEventFromRace = ({
  state,
  previousRaceEvent,
  nextRaceEvent
}: {
  state: GameState
  previousRaceEvent?: NormalizedRaceEvent
  nextRaceEvent: NormalizedRaceEvent
}): void => {
  if (!previousRaceEvent) {
    return
  }

  if (previousRaceEvent.uuid !== nextRaceEvent.uuid) {
    console.error('Should not happen, prev and next race event are different:', {
      previousRaceEvent,
      nextRaceEvent
    })
    return
  }

  const race = state.races.byId[previousRaceEvent.raceId]

  if (!race) {
    console.error('Should not happen, race not found for race event:', {
      previousRaceEvent,
      nextRaceEvent
    })
    return
  }

  if (previousRaceEvent.raceId !== nextRaceEvent.raceId) {
    // remove race result from old race
    race.raceEventIds = race.raceEventIds.filter((id) => id !== nextRaceEvent.uuid)
  }
}

const setRaces = (state: GameState, races: NonEmptyArray<Race>) => {
  races.forEach((race) => {
    const normalized = RaceMap.toNormalizedRace(race)
    const known = state.races.byId[race.uuid]

    // MERGE the event ids rather than replacing them. This write is fed by a
    // Supabase refetch that re-runs on focus/visibilitychange/pageshow/online,
    // and its response is a SNAPSHOT: if a socket event landed while the
    // request was in flight, a wholesale assignment REWINDS the round to
    // before that event. The phase then moves backwards, and the banner
    // announces it again — "place your bets" replaying seconds after betting
    // actually closed. A fetch may add to what we know, never shrink it.
    // (Event ids are re-sorted by timestamp where the selector reads them, so
    // only membership matters here.)
    normalized.raceEventIds = unionFetchedIds(normalized.raceEventIds, known?.raceEventIds)

    state.races.byId[race.uuid] = normalized

    // Add race id if it doesn't exist
    if (!state.races.allIds.includes(race.uuid)) {
      state.races.allIds.unshift(race.uuid)
    }
  })

  return state
}

const setGame = (state: GameState, game: MarbleGame): void => {
  // Set current game id
  // state.currentGameId = game.id // We can't do this otherwise can't reuse this function in our setGames state

  // Add game to normalized list
  const normalizedGame = GameMap.toNormalizedMarbleGame(game)
  const knownGame = state.games.byId[game.id]

  // Merge the ROUND list for the same reason as the event list above, but
  // ordered newest-first because this order is semantic: the selector maps
  // raceIds in stored order and the app treats races[0] as the current round.
  // A lagging snapshot that omits the round the socket just delivered would
  // otherwise roll the pointer back onto the previous, settled round — and the
  // next event rolls it forward again, announcing a phase twice per refocus.
  // setRaces runs below, so the incoming races are not in byId yet; read
  // createdAt from the payload first and fall back to what is already stored.
  // Newest-first, matching `orderRacesByCreatedAt` (a reversed string order on
  // createdAt) which the socket path already uses in addRaceToExistingGame.
  const incomingCreatedAt = new Map(game.races.map((race) => [race.uuid, race.createdAt]))
  const mergedRaceIds = mergeRaceIdsByRecency(
    normalizedGame.raceIds,
    knownGame?.raceIds,
    (id) => incomingCreatedAt.get(id) ?? state.races.byId[id]?.createdAt
  )
  // The union of a non-empty list is non-empty; the guard is for the type, and
  // keeps the incoming list untouched in the impossible case rather than
  // asserting past it.
  if (A.isNonEmptyArray(mergedRaceIds)) {
    normalizedGame.raceIds = mergedRaceIds
  }

  state.games.byId[game.id] = normalizedGame

  // Add game id if it doesn't exist
  if (!state.games.allIds.includes(game.id)) {
    state.games.allIds.unshift(game.id)
  }

  setRaces(state, game.races)

  // Add track to normalized list
  const normalizedTrack = TrackMap.toNormalizedTrack(game.track)
  state.tracks.byId[game.track.uuid] = normalizedTrack

  // Add track id if it doesn't exist
  if (!state.tracks.allIds.includes(game.track.uuid)) {
    state.tracks.allIds.unshift(game.track.uuid)
  }

  state.trackModes.byId[game.track.mode.uuid] = TrackModeMap.toNormalizedTrackMode(
    game.track.mode,
    game.track.uuid
  )
  // Add track m id if it doesn't exist
  if (!state.trackModes.allIds.includes(game.track.mode.uuid)) {
    state.trackModes.allIds.unshift(game.track.mode.uuid)
  }

  game.marbles.map((marble) => {
    state.marbles.byId[marble.uuid] = MarbleMap.toNormalizedMarble(marble)

    // Add marble id if it doesn't exist
    if (!state.marbles.allIds.includes(marble.uuid)) {
      state.marbles.allIds.unshift(marble.uuid)
    }
  })

  game.races.forEach((race) => {
    race.raceEvents.forEach((raceEvent) => {
      state.raceEvents.byId[raceEvent.uuid] = RaceEventMap.toNormalizedRaceEvent(raceEvent)

      // Add race event id if it doesn't exist
      if (!state.raceEvents.allIds.includes(raceEvent.uuid)) {
        state.raceEvents.allIds.unshift(raceEvent.uuid)
      }
    })
  })
}

export const useGameStore = create<GameStore>()(
  devtools(
    immer((set) => ({
      currentGameId: undefined,
      currentLanguage: getQueryParam('language') || fallbackLng,
      config: {},
      games: {
        isFetched: false,
        byId: {},
        allIds: [],
        playableIds: [],
        allowedGameIds: undefined
      },
      races: {
        byId: {},
        allIds: []
      },
      raceEvents: {
        byId: {},
        allIds: []
      },
      tracks: {
        byId: {},
        allIds: []
      },
      trackModes: {
        byId: {},
        allIds: []
      },
      marbles: {
        byId: {},
        allIds: []
      },
      actions: {
        raceEventAdded: (raceEvent) => {
          set(
            (state) => {
              // console.info('[raceEventAdded] triggered:', raceEvent)
              addRaceEvent(raceEvent)(state)
            },
            undefined,
            { type: 'raceEventAdded', raceEvent } as any
          )
        },
        raceEventUpdated: (raceEvent) => {
          set(
            (state) => {
              // console.info('[raceEventUpdated] triggered:', raceEvent)
              if (!state.raceEvents.byId[raceEvent.uuid]) return state

              const existingRaceEvent = original(state.raceEvents.byId[raceEvent.uuid]) ?? undefined
              state.raceEvents.byId[raceEvent.uuid] = RaceEventMap.toNormalizedRaceEvent(raceEvent)

              removeRaceEventFromRace({
                state,
                previousRaceEvent: existingRaceEvent,
                nextRaceEvent: raceEvent
              })

              const race = state.races.byId[raceEvent.raceId]

              if (!race) return state

              if (!race.raceEventIds.includes(raceEvent.uuid)) {
                addRaceEventToExistingRace(state, raceEvent)
              }
            },
            undefined,
            { type: 'raceEventUpdated', raceEvent } as any
          )
        },
        gameUpdated: (partialGame) => {
          set(
            (state) => {
              const existingGame = state.games.byId[partialGame.id]

              if (!existingGame) {
                return state
              }

              state.games.byId[partialGame.id] = {
                ...existingGame,
                ...partialGame
              }
            },
            undefined,
            'gameUpdated'
          )
        },
        raceAdded: (race) => {
          set(
            (state) => {
              // A re-delivered `race:created` for a round we already know
              // (common on mobile reconnect / tab refocus) must be a NO-OP:
              // re-running unshifts a duplicate id into races.allIds AND
              // re-fires the social store's raceChanged, which used to wipe the
              // current round's collected bets into the Previous tab ("shows 1
              // of my 2 bets"). Genuine updates to a known race arrive via
              // raceUpdated, not here. Defense-in-depth with raceChanged's own
              // idempotency guard.
              if (state.races.byId[race.id]) {
                return
              }
              // Get race events
              const raceEventIds = pipe(
                Object.values(state.raceEvents.byId),
                A.filter((raceEvent) => raceEvent.raceId === race.id),
                A.sortBy(orderRaceEventsByTimestamp),
                A.map((raceEvent) => raceEvent.uuid)
              )

              const enrichedNormalizedRace = {
                ...race,
                raceEventIds
              }

              state.races.byId[race.id] = enrichedNormalizedRace
              state.races.allIds.unshift(race.id)
              addRaceToExistingGame(state, enrichedNormalizedRace)

              // Notify social store that the round changed — but ONLY for the
              // game the player is actually on. With the tracks panel open the
              // race listeners run unfiltered (so the panel previews stay
              // fresh), which means race:created events from OTHER games land
              // here too; rotating the social store onto a foreign round id
              // wiped the current game's collected bets into the Previous tab
              // (a long-standing "feed randomly empties" contributor).
              if (race.raceConfigurationId === state.currentGameId) {
                const { actions } = useSocialStore.getState()
                // fromRaceCreated: this id is socket-fresh (the dedup above
                // guarantees it was never seen), which is what authorizes the
                // snapshot-misclassification promotion in the social store.
                actions.raceChanged(race.id, { fromRaceCreated: true })
              }
            },
            undefined,
            { type: 'raceAdded', race } as any
          )
        },
        raceUpdated: (race) => {
          set(
            (state) => {
              // `original()` throws if handed a non-draft. When switching
              // tracks the race isn't in the store yet, so guard the
              // lookup and treat a missing race as "no existing race".
              const draftRace = state.races.byId[race.id]
              const existingRace = draftRace ? original(draftRace) : undefined

              const updatedRace: NormalizedRace = existingRace
                ? { ...existingRace, ...race }
                : ({ ...race, raceEventIds: [] } as NormalizedRace)

              state.races.byId[race.id] = updatedRace

              if (!existingRace) {
                state.races.allIds.unshift(race.id)
              }

              // This can be the FIRST time the client sees a round: joining
              // the race channel mid-round means that round's `race:created`
              // was broadcast before we were listening, so `race:updated`
              // arrives first and inserts it above. The later `race:created`
              // then hits raceAdded's already-known no-op, so nothing ever
              // told the social store the round moved — its pointer stayed on
              // the previous round and EVERY player's bets for this one were
              // filed into the invisible pendingByRound buffer, then pruned
              // after three rounds. A refresh was the only cure.
              //
              // Only announce a round we just inserted. An update to a known
              // round is an ordinary update, and rotating there would wipe the
              // live round's collected bets into the Previous tab — the "shows
              // 1 of my 2 bets" regression raceAdded's guard exists to prevent.
              //
              // Same current-game scoping as raceAdded: with the tracks panel
              // open these listeners run unfiltered, and rotating onto a
              // foreign game's round empties this game's feed.
              //
              // fromRaceCreated stays FALSE: this is socket-fresh, but it is
              // not a `race:created`, and the promotion branch it unlocks is a
              // repair for a different case. The plain forward rotation is
              // what fixes this one, and it drains the buffer on the way.
              //
              // And only FORWARD. raceChanged's rotation branch moves allBets
              // into previousBets for any round that is not the current or the
              // immediately-previous one — it trusts its callers to announce
              // real forward moves. raceAdded can be trusted there because
              // `race:created` is a new round by definition; `race:updated`
              // cannot, because a late update for a round older than the ones
              // we fetched is also "unknown", and announcing it would roll the
              // pointer backwards and wipe the live round's bets.
              //
              // A missing createdAt means we cannot prove direction. That is
              // only the `lightning:revealed` partial, which is by
              // construction the LIVE round — announcing it is either a no-op
              // (pointer already correct) or the repair we want.
              if (!existingRace && race.raceConfigurationId === state.currentGameId) {
                const socialState = useSocialStore.getState()
                const currentPointer = socialState.raceId
                const pointerCreatedAt = currentPointer
                  ? state.races.byId[currentPointer]?.createdAt
                  : undefined

                if (movesPointerForward(race.createdAt, pointerCreatedAt)) {
                  socialState.actions.raceChanged(race.id, { fromRaceCreated: false })
                }
              }

              removeRaceFromGame({
                state,
                previousRace: existingRace,
                nextRace: updatedRace
              })

              const game = race.raceConfigurationId && state.games.byId[race.raceConfigurationId]

              if (!game) {
                return state
              }

              // Add race id to existing game
              if (!game.raceIds.includes(race.id)) {
                addRaceToExistingGame(state, updatedRace)
              }
            },
            undefined,
            { type: 'raceUpdated', race } as any
          )
        },
        setRaces: (gameId, races) => {
          set(
            (state) => {
              setRaces(state, races)

              state.actions.updateGameById({ id: gameId, races })
            },
            undefined,
            { type: 'setRaces', gameId, races } as any
          )
        },
        setCurrentGame: (game) => {
          set(
            (state) => {
              const socialState = useSocialStore.getState()
              const socialStreamState = useSocialStreamStore.getState()

              // Set current game id to undefined if game is unknown
              if (isUnknownGame(game)) {
                socialState.actions.removeUnprocessedBets()
                state.currentGameId = undefined
                return
              }

              // Reset config when switching games (new config arrives via WebSocket)
              if (state.currentGameId !== game.id) {
                state.config = {
                  ...state.config,
                  bettingMode: undefined,
                  markets: undefined,
                  bet: undefined
                } as any
              }

              // Set current game id
              setGame(state, game)
              state.currentGameId = game.id

              // Notify social store that game has changed
              const race = game.races[0]
              if (socialState.gameId !== game.id) {
                socialState.actions.gameChanged(game.id, race.uuid)
              }

              if (socialStreamState.gameId !== game.id) {
                socialStreamState.actions.gameChanged(game.id)
              }

              // FORWARD-ONLY, for the same reason raceUpdated is (v1.0.173) —
              // this is the path that fix did not cover.
              //
              // setCurrentGame runs off a Supabase READ-REPLICA fetch
              // (`<ref>-all`, ordered created_at desc) that useSyncWithRetry
              // re-triggers on visibilitychange / focus / pageshow / online. A
              // replica trailing the socket by a round therefore hands us a
              // STALE round every time the player tabs back in, and
              // raceChanged's rotation branch would file the LIVE round's bets
              // into previousBets and leave the Current tab empty.
              //
              // A game switch is exempt: the pointer then belongs to the game
              // being left, so comparing timestamps across games proves
              // nothing, and gameChanged above has already moved the pointer.
              if (socialState.raceId !== race.uuid) {
                const pointerCreatedAt = socialState.raceId
                  ? state.races.byId[socialState.raceId]?.createdAt
                  : undefined
                const switchedGame = socialState.gameId !== game.id

                if (switchedGame || movesPointerForward(race.createdAt, pointerCreatedAt)) {
                  socialState.actions.raceChanged(race.uuid)
                }
              }
            },
            undefined,
            { type: 'setCurrentGame', game } as any
          )
        },
        setGames: (games) => {
          set(
            (state) => {
              const ids = Object.keys(games)

              ids.forEach((id) => {
                state.games.byId[id] = GameMap.toNormalizedMarbleGame(games[id]!)
              })

              state.games.allIds = ids

              // races...
              Object.values(games).forEach((game) => {
                setGame(state, game)
              })
            },
            undefined,
            { type: 'setGames', games } as any
          )
        },
        setPlayableGames: (games, currentGameId) => {
          set(
            (state) => {
              const ids = Object.keys(games)
              state.games.playableIds = ids

              Object.values(games).forEach((game) => {
                if (game.id !== currentGameId) setGame(state, game)
              })

              // Mark as fetched
              state.games.isFetched = true
            },
            undefined,
            { type: 'setPlayableGames', games } as any
          )
        },
        updateGameById: (newGameData: Partial<MarbleGame> & { id: string }) => {
          set(
            (state) => {
              const gameToUpdateNormalized = state.games.byId[newGameData.id]

              if (!gameToUpdateNormalized) {
                // console.info('Cannot update non-existent game')
                return state
              }

              const oldGame = getGameSelector(state, gameToUpdateNormalized.id)

              if (!isMarbleGame(oldGame)) {
                console.error('Should not happen: game is not a MarbleGame', oldGame)
                return state
              }

              const updatedGame: MarbleGame = {
                ...oldGame,
                ...newGameData
              }

              state.games.byId[newGameData.id] = GameMap.toNormalizedMarbleGame(updatedGame)
            },
            undefined,
            { type: 'updateGameById', newGameData } as any
          )
        },
        updateTrack: (trackDTO: RealtimeTrackDTO) => {
          set(
            (state) => {
              const trackMode = pipe(
                state.trackModes.allIds,
                A.map((id) => state.trackModes.byId[id]),
                A.map(O.fromNullable),
                A.getSomes,
                A.filter((mode) => mode.active),
                A.findFirst((mode) => mode.trackId === trackDTO.id)
              )

              if (O.isNone(trackMode)) {
                // console.info('No active track mode found for track', trackDTO.id)
                return state
              }

              const normalizedTrack: NormalizedTrack = {
                uuid: trackDTO.id,
                name: trackDTO.name,
                gameType: trackDTO.game_type,
                shortName: trackDTO.short_name,
                description: trackDTO.description,
                image: trackDTO.image,
                poster: trackDTO.poster,
                background: trackDTO.background,
                videoPreviewMp4: normalizeNullableVideo(trackDTO.video_preview_mp4),
                videoPreviewWebm: trackDTO.video_preview_webm,
                createdAt: trackDTO.created_at,
                active: trackDTO.active,
                showInLobby: trackDTO.show_in_lobby,
                modeId: trackMode.value.uuid
              }

              state.tracks.byId[trackDTO.id] = normalizedTrack
            },
            undefined,
            { type: 'updateTrack', trackDTO } as any
          )
        },
        updateTrackMode: (trackModeDTO: RealtimeTrackModeDTO) => {
          set(
            (state) => {
              const trackMode = TrackModeMap.fromTrackModeDTO(trackModeDTO)

              if (trackModeDTO.track_id) {
                state.trackModes.byId[trackModeDTO.id] = TrackModeMap.toNormalizedTrackMode(
                  trackMode,
                  trackModeDTO.track_id
                )
              }
            },
            undefined,
            { type: 'updateTrackMode', trackModeDTO } as any
          )
        },
        tokenGameConfigRetrieved: (tokenGameConfig: TokenGameConfigDTO) => {
          set(
            (state) => {
              state.config = {
                ...state.config,
                lobbyDisabled: tokenGameConfig.lobbyDisabled,
                externalDepositUrl: tokenGameConfig.externalDepositUrl,
                externalLobbyUrl: tokenGameConfig.externalLobbyUrl,
                platform: tokenGameConfig.platform,
                language: tokenGameConfig.language,
                country: tokenGameConfig.country
              }
              if (tokenGameConfig.language) {
                state.currentLanguage = tokenGameConfig.language
              }
            },
            undefined,
            { type: 'tokenGameConfigRetrieved', tokenGameConfig } as any
          )
        },
        setAllowedGameIds: (ids: string[]) => {
          set(
            (state) => {
              state.games.allowedGameIds = ids
            },
            false,
            { type: 'setAllowedGameIds', ids } as any
          )
        },
        setCurrentLanguage: (language: string) => {
          set(
            (state) => {
              state.currentLanguage = language
            },
            undefined,
            { type: 'setCurrentLanguage', language } as any
          )
        },
        setGameConfig: (dto: GameConfigDTO) => {
          set(
            (state) => {
              const config = {
                ...state.config,
                // Unconditional: `...state.config` above would otherwise let a
                // previous game's rate leak into a game/currency that has none.
                usdRate: dto.usdRate,
                ...(dto.bettingMode && { bettingMode: dto.bettingMode }),
                ...(dto.markets && { markets: dto.markets }),
                bet: {
                  currency: dto.bet.currency,
                  limits: dto.bet.limits,
                  probabilities: dto.bet.probabilities,
                  maxBetAmount: dto.bet.maxBetAmount,
                  minBetAmount: dto.bet.minBetAmount,
                  defaultBetMultiplier:
                    dto.bet.defaultBetMultiplier && dto.bet.defaultBetMultiplier > 0
                      ? dto.bet.defaultBetMultiplier
                      : 5,
                  chipMultipliers: dto.bet.chipMultipliers ?? [
                    0.1, 0.5, 1, 5, 10, 25, 50, 100, 250, 500, 1000
                  ],
                  maxMultiplier: dto.bet.maxMultiplier,
                  maxPayout: dto.bet.maxPayout,
                  equalOdds: hasEqualOdds(dto.bet.probabilities)
                }
              }
              state.config = config
            },
            undefined,
            { type: 'setGameConfig', dto } as any
          )
        }
      }
    })),
    { name: 'GameStore', enabled: isDevtoolsEnabled() }
  )
)

export const useGameActions = () => useGameStore((state) => state.actions)

export const useGetGameConfig = () => useGameStore((state) => state.config)

export const useGetCurrentGameId = () => useGameStore((state) => state.currentGameId)

export const useGetGames = () => useGameStore((state) => state.games.byId)

export const useGetGameIds = () => useGameStore((state) => state.games.allIds)

export const useGetPlayableIds = () => useGameStore((state) => state.games.playableIds)

export const useAreAllGamesFetched = () => useGameStore((state) => state.games.isFetched)

export const useAreAllowedGamesFetched = () =>
  useGameStore((state) => isDefined(state.games.allowedGameIds))

export const useIsGameAccessRestricted = (gameId?: string) => {
  return useGameStore((state) => {
    const { allowedGameIds } = state.games

    if (!isDefined(allowedGameIds)) return false
    if (allowedGameIds.length === 0) return false
    if (gameId) return !allowedGameIds.includes(gameId)

    return true
  })
}

export const useGetCurrentLanguage = () => useGameStore((state) => state.currentLanguage)

export const useGetGameBetConfig = (player: Player) => {
  return useGameStore((state): GameConfig['bet'] | undefined => {
    const gameConfig = state.config

    if (!isKnownGameConfig(gameConfig)) {
      return undefined
    }

    if (!isAuthenticatedPlayer(player)) {
      return undefined
    }

    if (gameConfig.bet.currency !== player.balance.currency) {
      return undefined
    }

    return gameConfig.bet
  })
}

export const useGetCurrentGame = (): Game => {
  return useGameStore((state) => {
    const currentGame = state.currentGameId && state.games.byId[state.currentGameId]

    if (!currentGame) {
      return unknownGame
    }

    return getGameSelector(state, currentGame.id)
  })
}

export const useGetGameById = (gameId?: string) =>
  useGameStore((state) => {
    if (!gameId) {
      return unknownGame
    }

    const normalizedGame = state.games.byId[gameId]

    if (!normalizedGame) {
      return unknownGame
    }

    return getGameSelector(state, normalizedGame.id)
  })

export const useGetAllowedGameIds = () => useGameStore((state) => state.games.allowedGameIds)

export const useGetPlayableGames = (filterScheduled = false) => {
  return useGameStore((state) => {
    const playableIds = state.games.playableIds

    return playableIds
      .map((gameId) => {
        if (!gameId) return unknownGame

        const normalizedGame = state.games.byId[gameId]
        if (!normalizedGame) return unknownGame

        return getGameSelector(state, normalizedGame.id)
      })
      .filter(isMarbleGame)
      .filter((game) => {
        if (!filterScheduled) return true
        return getLatestEvent(game.races[0].raceEvents)?.eventType !== 'race_scheduled'
      })
      .sort((a, b) => {
        const eventA = getLatestEvent(a.races[0].raceEvents)
        const eventB = getLatestEvent(b.races[0].raceEvents)

        const isScheduledA = eventA?.eventType === 'race_scheduled'
        const isScheduledB = eventB?.eventType === 'race_scheduled'

        if (isScheduledA && !isScheduledB) return 1
        if (!isScheduledA && isScheduledB) return -1

        if (a.track.name === 'Rush Hour' || a.track.name === 'Duck River') return -1
        if (b.track.name === 'Rush Hour' || b.track.name === 'Duck River') return 1

        return a.track.name.localeCompare(b.track.name)
      })
  })
}

// This selector is used in different Zustand selectors, so it is not memoized
// By using reselect, we can memoize parts of logic
const getGameSelector = createSelector(
  [
    (state: GameState) => state.games,
    (state: GameState) => state.marbles,
    (state: GameState) => state.races,
    (state: GameState) => state.raceEvents,
    (state: GameState) => state.tracks,
    (state: GameState) => state.trackModes,
    (_state: GameState, gameId: string) => gameId
  ],
  (games, marbles, races, raceEvents, tracks, trackModes, gameId: string) => {
    const game = games.byId[gameId]

    if (!game || !game.active) {
      return unknownGame
    }

    const marblesData = pipe(
      game.marbleIds,
      A.map((id) => marbles.byId[id]),
      A.map(O.fromNullable),
      A.getSomes,
      A.map(MarbleMap.fromNormalizedMarble)
    )

    const racesData = pipe(
      game.raceIds,
      A.map((id) => races.byId[id]),
      A.map(O.fromNullable),
      A.getSomes,
      A.map((normalizedRace) => {
        const raceEventsData = pipe(
          normalizedRace.raceEventIds,
          A.map((id) => raceEvents.byId[id]),
          A.map(O.fromNullable),
          A.getSomes,
          A.sortBy(orderRaceEventsByTimestamp),
          A.map(RaceEventMap.fromNormalizedRaceEvent)
        )

        if (raceEventsData.length === 0) {
          return null
        }

        const race: Race = {
          uuid: normalizedRace.id,
          round: normalizedRace.round,
          createdAt: normalizedRace.createdAt,
          raceConfigurationId: game.id,
          raceEvents: raceEventsData,
          isLightningRound: normalizedRace.isLightningRound,
          lightningBoostType: normalizedRace.lightningBoostType,
          lightningBoostPercent: normalizedRace.lightningBoostPercent,
          lightningBoostedItems: normalizedRace.lightningBoostedItems
        }

        return race
      }),
      A.map(O.fromNullable),
      A.getSomes
    )

    if (!A.isNonEmptyArray(racesData)) {
      // Expected for games whose races aren't loaded yet — e.g. the
      // lobby and the track-selection overlay preview other games
      // without full race data. Fall back to unknownGame silently;
      // this isn't an error condition.
      return unknownGame
    }

    const trackOpt = pipe(
      O.fromNullable(tracks.byId[game.trackId]),
      O.filter((track) => track.active),
      O.flatMap((normalizedTrack) =>
        pipe(
          O.fromNullable(trackModes.byId[normalizedTrack.modeId]),
          O.filter((trackMode) => trackMode.active),
          O.map(TrackModeMap.fromNormalizedTrackMode),
          O.map((trackMode) => TrackMap.fromNormalizedTrack(normalizedTrack, trackMode))
        )
      )
    )

    if (O.isNone(trackOpt)) {
      return unknownGame
    }

    const marbleGame: MarbleGame = {
      type: 'MarbleGame',
      id: game.id,
      name: game.name,
      laps: game.laps,
      totalMarbles: game.totalMarbles,
      maxPickableMarbles: game.maxPickableMarbles,
      bettingOpenWindow: game.bettingOpenWindow,
      bettingClosingWindow: game.bettingClosingWindow,
      settledWindow: game.settledWindow,
      resultsToSubmit: game.resultsToSubmit,
      active: game.active,
      createdAt: game.createdAt,
      previewThumbnail: game.previewThumbnail,
      marbles: marblesData,
      races: racesData,
      track: trackOpt.value
    }

    return marbleGame
  }
)

const addRaceEvent = (raceEvent: RaceEvent) => (state: GameState) => {
  const normalizedRaceEvent = RaceEventMap.toNormalizedRaceEvent(raceEvent)
  state.raceEvents.byId[raceEvent.uuid] = normalizedRaceEvent
  state.raceEvents.allIds.unshift(raceEvent.uuid)

  const race = state.races.byId[raceEvent.raceId]

  if (!race) {
    return
  }

  const sortedRaceEventIds = pipe(
    race.raceEventIds,
    A.map((id) => state.raceEvents.byId[id]),
    A.append(normalizedRaceEvent),
    A.map(O.fromNullable),
    A.getSomes,
    A.sortBy(orderRaceEventsByTimestamp),
    A.map((re) => re.uuid)
  )

  state.races.byId[race.id] = {
    ...race,
    raceEventIds: sortedRaceEventIds
  }
}

const hasEqualOdds = (probabilities?: Record<string, number>): boolean => {
  if (!probabilities) return true

  const keys = Object.keys(probabilities)

  // If probabilities object is empty, treat it as having equal odds
  if (keys.length === 0) return true

  const values = Object.values(probabilities)
  const firstValue = values[0]

  // Check if all values are equal to the first value
  return values.every((value) => value === firstValue)
}

export const __TEST__ = {
  getGameSelector
}
